import React, { useEffect, useState } from 'react';

import { useNEFISteps } from 'context/NEFIStepsContext';
import { useFormik } from 'formik';
import { useFormUtilityData } from 'hooks/useFormUtilityData';
import PropTypes from 'prop-types';

import { ActionButton } from 'components/ActionButton/ActionButton';
import { SectionContent } from 'components/DEBForm/SectionContent';
import { FormPageWrapper } from 'components/FormHelper/FormPageWrapper';
import { FormSection } from 'components/FormHelper/FormSection';
import { PlusIcon } from 'components/Icons/PlusIcon';
import { LoadingIndicator } from 'components/LoadingIndicator/LoadingIndicator';
import { NEFIExpertTable } from 'components/NEFIComponents/NEFIExpertsTable/NEFIExpertsTable';
import { NEFINewExpertPopup } from 'components/NEFIComponents/NEFINewExpertPopup/NEFINewExpertPopup';
import { NEFIAgreementSection } from 'components/NEFIForm/NEFIAgreementSection';
import {
  NEFI_CURRENT_TEST_SUITE,
  shouldFillNEFITestData,
} from 'components/NEFIForm/NEFITestdata/NEFITestSetup';
import { NEFI_ERROR_MESSAGE } from 'components/NEFIForm/Utils/NEFIError';
import { YNEFIStepFormPageSchema } from 'components/NEFIValidation/YNEFIStepFormPageSchema';

import { NE_ACTIVE_STEP } from 'global/constants';
import { evaluateCondition } from 'global/fieldValidationFunction';

import { FFormDebug } from 'utils/FFormDebug';
import { checkKeyDown } from 'utils/FormUtils';
import { isRegisteringInstallerOnlyHilfsbetrieb } from 'utils/InstallerUtils';

import { NEFIFormIV } from './NEFIFormIV';
import { NEFI_FIRST_SECTION_INPUTS, checkErrors } from './NEFIFormUtility';

export const NEFIFormPage = ({ energyAgreementLink, gasAgreementLink, setButtonErrorMessage }) => {
  const { setSubmittedValuesWithStep, getSubmittedValuesWithStep } = useNEFISteps();
  const {
    comboBoxOptions,
    gridnetProviders,
    isLoading: isLoadingFormUtilityData,
  } = useFormUtilityData();

  const branchSelectionValues = getSubmittedValuesWithStep(NE_ACTIVE_STEP.BRANCH_SELECTION);

  const [newExpertPopupOpen, setNewExpertPopupOpen] = useState(false);
  const [errors, setErrors] = useState([]);

  const initialValues = shouldFillNEFITestData
    ? NEFI_CURRENT_TEST_SUITE.step2
    : NEFIFormIV(
        getSubmittedValuesWithStep(NE_ACTIVE_STEP.EMAIL_INPUT),
        getSubmittedValuesWithStep(NE_ACTIVE_STEP.BRANCH_SELECTION),
        getSubmittedValuesWithStep(NE_ACTIVE_STEP.FORM)
      );

  const formik = useFormik({
    validationSchema: YNEFIStepFormPageSchema(
      getSubmittedValuesWithStep(NE_ACTIVE_STEP.BRANCH_SELECTION)
    ),
    initialValues,
    enableReinitialize: true,
    validateOnMount: true,
    validateOnChange: true,
  });

  const { values } = formik;

  useEffect(() => {
    setSubmittedValuesWithStep(NE_ACTIVE_STEP.FORM, values);
  }, [values]);

  useEffect(() => {
    if (isLoadingFormUtilityData) {
      return undefined;
    }

    let cancelled = false;

    formik.validateForm().then((validationErrors) => {
      if (cancelled) {
        return;
      }

      const realErrors = checkErrors(values, branchSelectionValues, validationErrors);
      setErrors(realErrors);
      setButtonErrorMessage(realErrors.length > 0 ? NEFI_ERROR_MESSAGE : '');
    });

    return () => {
      cancelled = true;
    };
  }, [values, isLoadingFormUtilityData, branchSelectionValues]);
  if (isLoadingFormUtilityData) {
    return <LoadingIndicator />;
  }
  return (
    <form
      onKeyDown={checkKeyDown}
      onSubmit={(e) => {
        e.preventDefault();
      }}>
      <FormPageWrapper>
        {NEFI_FIRST_SECTION_INPUTS.map((section) => {
          const evaluateConditionOptions = {
            isOnlyHilfsbetrieb: isRegisteringInstallerOnlyHilfsbetrieb(branchSelectionValues),
            branchSelectionValues,
            values,
          };
          if (evaluateCondition(section.whenShouldSectionRender, evaluateConditionOptions)) {
            return (
              <FormSection
                key={section.id}
                title={section.title}
                subtitle={section.subtitle}
                infoText={section.infoText}
                tooltip={section.tooltip}
                navigationTooltip={section.navigationTooltip}>
                <SectionContent
                  section={section}
                  formik={formik}
                  values={values}
                  initialValues={initialValues}
                  comboBoxOptions={comboBoxOptions}
                  evaluateConditionOptions={evaluateConditionOptions}
                />
              </FormSection>
            );
          }
          return null;
        })}
        <FormSection title="Fachinteressenten">
          <NEFIExpertTable
            formik={formik}
            errors={errors}
            branchSelectionValues={getSubmittedValuesWithStep(NE_ACTIVE_STEP.BRANCH_SELECTION)}
          />
          <ActionButton
            inverted
            icon={<PlusIcon />}
            title={<div className="text-nowrap"> Fachinteressent hinzufügen</div>}
            onClick={() => {
              setNewExpertPopupOpen(true);
            }}
          />
          <NEFINewExpertPopup
            branchSelectionValues={getSubmittedValuesWithStep(NE_ACTIVE_STEP.BRANCH_SELECTION)}
            popupOpen={newExpertPopupOpen}
            setPopupOpen={setNewExpertPopupOpen}
            mainFormik={formik}
          />
        </FormSection>
        <FormSection>
          <NEFIAgreementSection
            formik={formik}
            branchSelectionValues={getSubmittedValuesWithStep(NE_ACTIVE_STEP.BRANCH_SELECTION)}
            energyAgreementLink={energyAgreementLink}
            gasAgreementLink={gasAgreementLink}
          />
        </FormSection>
      </FormPageWrapper>
      <FFormDebug values={values} errors={formik.errors} touched={formik.touched} />
    </form>
  );
};

NEFIFormPage.propTypes = {
  energyAgreementLink: PropTypes.string,
  gasAgreementLink: PropTypes.string,
  setButtonErrorMessage: PropTypes.func.isRequired,
};

NEFIFormPage.defaultProps = { energyAgreementLink: '', gasAgreementLink: '' };
